import styled from '@emotion/styled';

import BaseLink from '@/components/common/BaseLink/BaseLink';
import {
  determineDestination,
  PortableText,
  VisuallyHidden,
} from '@/components/common/utils';
import { getComponent } from '@/components/page/blog/blogUtils';
import { headingLarge } from '@/styles/typography';

import type { BlogPostTypeExtended } from '@/pages/blog/[slug]';

export interface PostCopyProps {
  excerpt: BlogPostTypeExtended['postBody'];
  blogPost: BlogPostTypeExtended;
}

const getComponents = () => {
  const components = {
    marks: {
      strong: ({ children }) => <b>{children}</b>,
      internalLink: ({ value, children }) => {
        if (!value.item) return children;

        let prefix = '';

        switch (value.item._type) {
          case 'blogPost':
            prefix = '/blog';
            break;
          case 'programme': {
            const { audienceRef, region } = value.item;

            const basePath = audienceRef ? audienceRef.basePath : '';
            const programmesPath =
              region === 'en-GB' ? 'programmes' : 'programs';

            prefix = `${basePath}/${programmesPath}`;
            break;
          }
          case 'page':
          default: {
            const { audienceRef } = value.item;
            prefix = audienceRef?.basePath ?? '';
            break;
          }
        }

        const href = `${prefix}/${value.item.slug.current}`;

        return <BaseLink href={href}>{children}</BaseLink>;
      },
      link: ({ value, children }) => {
        const { href } = value;

        if (!href) {
          return <span>{children}</span>;
        }

        const destination = determineDestination(href);
        return (
          <BaseLink
            href={href}
            target={destination.target}
            rel={
              destination.isExternalLink ? 'external noopener noreferrer' : null
            }
          >
            {children}
            {destination.isExternalLink && (
              <VisuallyHidden>{destination.newTabText}</VisuallyHidden>
            )}
          </BaseLink>
        );
      },
    },
  };
  return components;
};

export const PostCopy = ({ excerpt, blogPost }: PostCopyProps) => {
  return (
    <PostCopyContainer>
      <StyledExcerptText>
        <PortableText value={excerpt} components={getComponents()} />
      </StyledExcerptText>
      {/* This takes the first paragraph of the body, and turns it into a subheading (excerpt) */}
      {getComponent(blogPost?.postBody.slice(1), blogPost)}
    </PostCopyContainer>
  );
};

const PostCopyContainer = styled.div``;

const StyledExcerptText = styled.div`
  ${headingLarge}

  a {
    font-size: inherit;
    font-weight: inherit;
  }

  padding-bottom: var(--spacing-x-large);
`;
